// faceGuide.js — optional, lazy-loaded face detection for the recorder's
// head-position guide. Uses MediaPipe Tasks Vision (loaded from CDN) so it
// never touches the main bundle. EVERYTHING here is best-effort: if the model
// or WASM fails to load, or the browser can't run it, the caller falls back to
// the static guide. The recorder must work exactly the same with this off.

const CDN = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18';
const MODEL = 'https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite';

let _detector = null;
let _loadPromise = null;

// Lazy-load the FaceDetector once. Returns null on any failure.
export async function loadFaceDetector() {
  if (_detector) return _detector;
  if (_loadPromise) return _loadPromise;
  _loadPromise = (async () => {
    try {
      const vision = await import(/* @vite-ignore */ `${CDN}/vision_bundle.mjs`);
      const { FaceDetector, FilesetResolver } = vision;
      const fileset = await FilesetResolver.forVisionTasks(`${CDN}/wasm`);
      _detector = await FaceDetector.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODEL, delegate: 'GPU' },
        runningMode: 'VIDEO',
        minDetectionConfidence: 0.5,
      });
      return _detector;
    } catch (e) {
      try { console.warn('faceGuide: detector unavailable, using static guide', e?.message); } catch {}
      return null;
    }
  })();
  return _loadPromise;
}

// Classify the largest detected face against the target oval.
// Returns { state, hint } where state ∈ 'good' | 'adjust' | 'none'.
//
// target = { cx, cy, h } as fractions of the video frame (oval center + height
// fraction). We compare the detected face box center + height to it.
export function classifyFace(detection, videoW, videoH, target) {
  if (!detection || !detection.detections || detection.detections.length === 0) {
    return { state: 'none', hint: 'Step into the frame' };
  }
  // Largest face by box area (handles a stray background face).
  let best = null, bestArea = 0;
  for (const d of detection.detections) {
    const b = d.boundingBox;
    const area = b.width * b.height;
    if (area > bestArea) { bestArea = area; best = b; }
  }
  if (!best) return { state: 'none', hint: 'Step into the frame' };

  // NOTE: the preview is mirrored (scaleX(-1)). The detector runs on the
  // unmirrored frame, so horizontal offset reads naturally for the user.
  const faceCx = (best.originX + best.width / 2) / videoW;
  const faceCy = (best.originY + best.height / 2) / videoH;
  const faceH  = best.height / videoH;   // face height as fraction of frame

  // Tolerances tuned for the ~52%-height oval guide.
  const TARGET_H = target.h;             // ideal face height fraction
  const offX = faceCx - target.cx;
  const offY = faceCy - target.cy;

  // Distance check first (most common problem).
  if (faceH < TARGET_H * 0.72) return { state: 'adjust', hint: 'Move a little closer' };
  if (faceH > TARGET_H * 1.35) return { state: 'adjust', hint: 'Move back a bit' };

  // Centering.
  if (Math.abs(offX) > 0.16) return { state: 'adjust', hint: offX > 0 ? 'Move left' : 'Move right' };
  if (offY < -0.16) return { state: 'adjust', hint: 'Lower the camera a little' };
  if (offY > 0.16)  return { state: 'adjust', hint: 'Raise the camera a little' };

  return { state: 'good', hint: "Perfect — you're set" };
}
